import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Device, OpsUser, Page, Passkey } from '@elutide/shared';
import { api, ApiProblem } from '@/lib/api';
import { qk } from '@/lib/keys';
import { useAuthMode } from '@/features/auth/mode';
import { AgeText } from '@/components/Provenance';
import {
  Button,
  Card,
  ConfirmDialog,
  DataTable,
  Drawer,
  EmptyState,
  ErrorState,
  Pill,
  StepUpGate,
  useToast,
  type Column,
} from '@/components';

type PendingAction =
  | { kind: 'reset_pin'; user: OpsUser }
  | { kind: 'deactivate'; user: OpsUser }
  | { kind: 'enroll_device'; user: OpsUser }
  | { kind: 'revoke_device'; user: OpsUser; device: Device }
  | { kind: 'revoke_passkey'; user: OpsUser; passkey: Passkey }
  | { kind: 'revoke_sessions'; user: OpsUser };

/**
 * Settings > Users and devices. Owner only, and every action here is tier 3: a fresh passkey
 * step-up, a typed confirmation and an audit row.
 *
 * Passkeys are the recommended path and the page says so plainly. A PIN unlocks a locked
 * session or signs in from a device already enrolled for that user, and can do nothing else:
 * it can never enroll a device or reset a passkey.
 *
 * In PIN mode (features/auth/mode.ts) there are no passkeys and no device enrollment, so
 * nothing here offers either. The page says so in one line instead.
 */
export const PASSKEYS_OFF_MESSAGE = 'Passkeys are turned off. Sign-in uses a PIN.';

export function Users() {
  const client = useQueryClient();
  const toast = useToast();
  const pinOnly = useAuthMode().mode === 'pin';
  const [selected, setSelected] = useState<OpsUser | null>(null);
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [enrollment, setEnrollment] = useState<{ code: string; expiresAt: string } | null>(null);

  const users = useQuery({
    queryKey: qk.users(),
    queryFn: () => api.get<Page<OpsUser>>('/users', { query: { limit: 50 } }),
  });

  const devices = useQuery({
    queryKey: qk.userDevices(selected?.user_id ?? 'none'),
    queryFn: () => api.get<Page<Device>>(`/users/${selected?.user_id ?? ''}/devices`),
    enabled: selected !== null && !pinOnly,
  });

  const passkeys = useQuery({
    queryKey: qk.userPasskeys(selected?.user_id ?? 'none'),
    queryFn: () => api.get<Page<Passkey>>(`/users/${selected?.user_id ?? ''}/passkeys`),
    enabled: selected !== null && !pinOnly,
  });

  // GET /users/:userId/devices returns revoked devices too, while device_count on the row
  // counts only live ones. Show the live ones, so the drawer and the table agree and nobody
  // is offered a Revoke button for a device that is already revoked.
  const liveDevices = (devices.data?.items ?? []).filter((device) => device.revoked_at === null);

  const refresh = (userId: string) => {
    void client.invalidateQueries({ queryKey: qk.users() });
    void client.invalidateQueries({ queryKey: qk.userDevices(userId) });
    void client.invalidateQueries({ queryKey: qk.userPasskeys(userId) });
  };

  const act = useMutation({
    mutationFn: async ({ action }: { action: PendingAction; typed: string }) => {
      switch (action.kind) {
        case 'reset_pin':
          return api.post(`/users/${action.user.user_id}/pin`, {
            typed_confirm: serverConfirm(action),
          });
        case 'deactivate':
          return api.patch(`/users/${action.user.user_id}`, {
            is_active: !action.user.is_active,
            typed_confirm: serverConfirm(action),
          });
        case 'enroll_device':
          return api.post<{ code: string; expires_at: string }>(
            `/users/${action.user.user_id}/devices/enroll`,
            { label: 'New device', typed_confirm: serverConfirm(action) },
          );
        case 'revoke_device':
          return api.del(
            `/users/${action.user.user_id}/devices/${action.device.device_id}`,
            { typed_confirm: serverConfirm(action) },
          );
        case 'revoke_passkey':
          return api.del(
            `/users/${action.user.user_id}/passkeys/${action.passkey.passkey_id}`,
            { typed_confirm: serverConfirm(action) },
          );
        case 'revoke_sessions':
          // Tier 2, no body and no typed confirmation on the API side.
          return api.del(`/users/${action.user.user_id}/sessions`);
        default:
          return undefined;
      }
    },
    onSuccess: (result, variables) => {
      refresh(variables.action.user.user_id);
      if (variables.action.kind === 'enroll_device' && result) {
        const typed = result as { code: string; expires_at: string };
        setEnrollment({ code: typed.code, expiresAt: typed.expires_at });
      }
      toast.push({ tone: 'ok', message: 'Done, and recorded in the audit log.' });
    },
    onError: (error) =>
      toast.push({
        tone: 'crit',
        message: error instanceof ApiProblem ? error.message : 'The change was refused.',
        requestId: error instanceof ApiProblem ? error.shortRequestId : null,
      }),
  });

  const columns: readonly Column<OpsUser>[] = [
    {
      id: 'name',
      header: 'User',
      priority: 1,
      render: (user) => (
        <span>
          {user.display_name} <span className="o-muted">{user.handle}</span>
        </span>
      ),
    },
    {
      id: 'role',
      header: 'Role',
      priority: 1,
      render: (user) => <Pill tone={user.role === 'owner' ? 'copper' : 'neutral'}>{user.role}</Pill>,
    },
    {
      id: 'auth',
      header: 'Auth',
      priority: 1,
      render: (user) => (
        <span className="o-row">
          {pinOnly ? null : (
            <Pill tone={user.passkey_count > 0 ? 'ok' : 'crit'}>
              {user.passkey_count} passkey{user.passkey_count === 1 ? '' : 's'}
            </Pill>
          )}
          <Pill tone={user.has_pin ? 'neutral' : 'warn'}>{user.has_pin ? 'PIN set' : 'no PIN'}</Pill>
        </span>
      ),
    },
    {
      id: 'devices',
      header: 'Devices',
      priority: 2,
      align: 'right',
      mono: true,
      render: (user) => <span>{user.device_count}</span>,
    },
    {
      id: 'last_login',
      header: 'Last login',
      priority: 2,
      render: (user) => <AgeText at={user.last_login_at} />,
    },
    {
      id: 'state',
      header: 'State',
      priority: 3,
      render: (user) => (
        <Pill tone={user.is_active ? 'ok' : 'warn'}>{user.is_active ? 'active' : 'disabled'}</Pill>
      ),
    },
    {
      id: 'locked',
      header: 'Lockout',
      priority: 4,
      render: (user) =>
        user.locked_until ? <Pill tone="crit">locked</Pill> : <span className="o-muted">none</span>,
    },
  ];

  return (
    <>
      <Card title="Users and devices" id="users">
        {users.isError ? (
          <ErrorState
            message={
              users.error instanceof ApiProblem ? users.error.message : 'Users did not load.'
            }
            onRetry={() => void users.refetch()}
          />
        ) : (
          <DataTable<OpsUser>
            caption="Ops users"
            columns={columns}
            rows={users.data?.items ?? []}
            rowKey={(user) => user.user_id}
            loading={users.isLoading}
            onRowClick={(user) => setSelected(user)}
            emptyState={<EmptyState message="No users yet." />}
          />
        )}
        {pinOnly ? (
          <p className="o-muted" data-testid="users-passkeys-off">
            {PASSKEYS_OFF_MESSAGE}
          </p>
        ) : (
          <p className="o-muted">
            A passkey is the recommended factor and the only way to enroll a device. A PIN
            unlocks a locked session and signs in from a device already enrolled for that user.
            It can never enroll a device, reset a passkey, or satisfy a tier 3 step-up.
          </p>
        )}
      </Card>

      <Drawer
        open={selected !== null}
        onClose={() => setSelected(null)}
        title={selected ? `${selected.display_name} (${selected.role})` : ''}
        id="user-drawer"
      >
        {selected && pinOnly ? (
          <p className="o-muted" data-testid="user-drawer-passkeys-off">
            {PASSKEYS_OFF_MESSAGE}
          </p>
        ) : null}
        {selected ? (
          <StepUpGate tier={3} action={pinOnly ? 'Changing a user' : 'Changing a user, a device or a passkey'}>
            {pinOnly ? null : (
            <section className="o-stack">
              <h3 className="o-section-title">Devices</h3>
              {liveDevices.length === 0 ? (
                <EmptyState message="No enrolled devices." />
              ) : (
                liveDevices.map((device) => (
                  <div className="o-kv" key={device.device_id}>
                    <span className="o-kv__key">
                      {device.label}
                      <span className="o-muted">
                        {' '}
                        last seen <AgeText at={device.last_seen_at} />
                      </span>
                    </span>
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => setPending({ kind: 'revoke_device', user: selected, device })}
                    >
                      Revoke
                    </Button>
                  </div>
                ))
              )}
              <Button
                variant="primary"
                onClick={() => setPending({ kind: 'enroll_device', user: selected })}
              >
                Issue an enrollment code
              </Button>
              {enrollment ? (
                <p className="o-notice">
                  One time code <strong className="o-mono">{enrollment.code}</strong>. Single use,
                  expires <AgeText at={enrollment.expiresAt} />. Read it to the person, do not
                  send it in a message you cannot delete.
                </p>
              ) : null}
            </section>
            )}

            {pinOnly ? null : (
            <section className="o-stack">
              <h3 className="o-section-title">Passkeys</h3>
              {(passkeys.data?.items ?? []).length === 0 ? (
                <EmptyState message="No passkeys. This user can only sign in from an enrolled device." />
              ) : (
                (passkeys.data?.items ?? []).map((passkey) => (
                  <div className="o-kv" key={passkey.passkey_id}>
                    <span className="o-kv__key">
                      {passkey.label}
                      <span className="o-muted">
                        {' '}
                        last used <AgeText at={passkey.last_used_at} />
                      </span>
                    </span>
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => setPending({ kind: 'revoke_passkey', user: selected, passkey })}
                    >
                      Remove
                    </Button>
                  </div>
                ))
              )}
            </section>
            )}

            <section className="o-stack">
              <h3 className="o-section-title">Recovery and sessions</h3>
              <div className="o-row">
                <Button onClick={() => setPending({ kind: 'reset_pin', user: selected })}>
                  Clear the PIN
                </Button>
                <Button onClick={() => setPending({ kind: 'revoke_sessions', user: selected })}>
                  Revoke every session
                </Button>
                <Button
                  variant={selected.is_active ? 'danger' : 'primary'}
                  onClick={() => setPending({ kind: 'deactivate', user: selected })}
                >
                  {selected.is_active ? 'Disable this user' : 'Enable this user'}
                </Button>
              </div>
            </section>
          </StepUpGate>
        ) : null}
      </Drawer>

      <ConfirmDialog
        open={pending !== null}
        title={pending ? titleFor(pending) : ''}
        body={pending ? bodyFor(pending, pinOnly) : ''}
        effects={pending ? effectsFor(pending, pinOnly) : []}
        confirmLabel="Confirm"
        typedConfirm={pending?.user.handle}
        reasonRequired
        destructive={pending?.kind !== 'enroll_device'}
        note={
          pinOnly
            ? 'Tier 3. Requires a fresh PIN confirmation, writes an audit row and posts to Slack.'
            : 'Tier 3. Requires a fresh passkey step-up, writes an audit row and posts to Slack.'
        }
        onCancel={() => setPending(null)}
        onConfirm={async () => {
          const action = pending;
          setPending(null);
          if (!action) return;
          await act.mutateAsync({ action, typed: action.user.handle });
        }}
      />
    </>
  );
}

/**
 * The exact string each tier 3 route demands in `typed_confirm`. The API compares it
 * literally (core/auth.ts assertTypedConfirm), so sending the operator's typed handle for
 * every action, as this page used to, failed every one of them with typed_confirm_required.
 * The handle stays as the human ceremony in the dialog; this is the machine token.
 */
function serverConfirm(action: PendingAction): string {
  switch (action.kind) {
    case 'reset_pin':
      return 'RESET PIN';
    case 'deactivate':
      // PATCH /users/:userId declares typedConfirm as the userId from the path.
      return action.user.user_id;
    case 'enroll_device':
      return 'ENROLL';
    case 'revoke_device':
    case 'revoke_passkey':
      return 'REVOKE';
    default:
      return '';
  }
}

function titleFor(action: PendingAction): string {
  switch (action.kind) {
    case 'reset_pin':
      return `Clear the PIN for ${action.user.display_name}?`;
    case 'deactivate':
      return action.user.is_active
        ? `Disable ${action.user.display_name}?`
        : `Enable ${action.user.display_name}?`;
    case 'enroll_device':
      return `Issue an enrollment code for ${action.user.display_name}?`;
    case 'revoke_device':
      return `Revoke ${action.device.label}?`;
    case 'revoke_passkey':
      return `Remove the passkey ${action.passkey.label}?`;
    case 'revoke_sessions':
      return `Revoke every session for ${action.user.display_name}?`;
    default:
      return '';
  }
}

function bodyFor(action: PendingAction, pinOnly: boolean): string {
  switch (action.kind) {
    case 'reset_pin':
      return pinOnly
        ? 'The PIN is cleared. The user needs a new PIN before they can sign in again. This never sets a PIN for another person.'
        : 'The PIN is cleared. The user signs in with a passkey and sets a new PIN afterwards. This never sets a PIN for another person.';
    case 'deactivate':
      return action.user.is_active
        ? 'The user cannot sign in and their live sessions end.'
        : 'The user can sign in again from their enrolled devices.';
    case 'enroll_device':
      return 'A single use code, valid for ten minutes, that binds one new device to this user during a passkey registration.';
    case 'revoke_device':
      return 'The device is revoked and its sessions end immediately.';
    case 'revoke_passkey':
      return 'The passkey is removed. If it is their last one they will need an owner issued enrollment code to get back in.';
    case 'revoke_sessions':
      return 'Every session for this user ends. They sign in again on their next request.';
    default:
      return '';
  }
}

function effectsFor(action: PendingAction, pinOnly: boolean): readonly string[] {
  switch (action.kind) {
    case 'reset_pin':
      return pinOnly
        ? ['PIN cleared', 'Sign in needs a new PIN', 'Audit row written']
        : ['PIN cleared', 'Passkey sign in required', 'Audit row written'];
    case 'deactivate':
      return ['Sign in blocked or restored', 'Live sessions ended', 'Audit row written'];
    case 'enroll_device':
      return ['A one time code is shown once', 'Valid for ten minutes', 'Single use'];
    case 'revoke_device':
      return ['Device revoked', 'Its sessions killed immediately', 'PIN login from it stops working'];
    case 'revoke_passkey':
      return ['Passkey removed', 'Refused if it is the last one and nobody can re-enroll them'];
    case 'revoke_sessions':
      return ['Every session ends', 'The user signs in again'];
    default:
      return [];
  }
}
